/* eslint-disable @typescript-eslint/explicit-function-return-type */
import type { H3Error } from 'h3'
import type { Event as SentryEvent } from '@sentry/vue'
import { once } from '~/utils/once'

type ErrorForNuxt = Exclude<Parameters<typeof createError>[0], string | Error>

export type AnyErrorData = { wasLoggedToSentry?: boolean, [k: string]: unknown }

type FormattedError = H3Error<AnyErrorData>

// we also ignore 400, because we get
// a lot of bot errors checking our endpoints for vulnerabilities
const IGNORE_ERROR_CODES = [404, 400]
export const ENVIRONMENTS_WITH_SENTRY_DISABLED = ['development', 'staging']

const BASE_ERROR_MESSAGE = 'Internal error'

// MAP to hide the original error message from the user
const ERROR_MESSAGE_BY_STATUS_CODE: Record<number, string> = {
  400: 'Bad request',
  404: 'Not found',
  500: BASE_ERROR_MESSAGE,
}

type CustomErrorData = { data: unknown, errors?: unknown }
export const extractErrorData = (e: unknown) => {
  if (typeof e === 'object' && e && 'data' in e && e.data) {
    const errorData: CustomErrorData = { data: e.data }

    // We put `errors` field on top level, because
    // Sentry gets lazy on nested objects and displays them as "[Object]"
    if (typeof e.data === 'object' && 'errors' in e.data) {
      errorData.errors = e.data.errors
    }
    return errorData
  }
  return undefined
}

export const getAdditionalErrorData = ({
  errorHandler,
  data,
}: {
  errorHandler: string
  data?: unknown
}) => ({ extra: { errorHandler, ...(typeof data === 'object' && { data }) } })

/**
 * Sets a `e.data.wasLoggedToSentry` flag to true,
 * to avoid duplicate logs of the same error
 */
export const updateWasLoggedToSentry = (e: unknown, value: boolean): void => {
  try {
    if (!e || typeof e !== 'object') {
      return
    }

    (e as { data?: AnyErrorData }).data ||= {}

    if (!('data' in e) || typeof e.data !== 'object') {
      return
    }

    (e.data as AnyErrorData).wasLoggedToSentry = value
  } catch (error) {
    /**
     * e.data could have no setter
     * (which is not a big issue, so we just log it)
     */
    // eslint-disable-next-line no-console
    console.error(error)
  }
}

/**
 * Checks if `e.data.wasLoggedToSentry === true`
 * (and `e.data.data.wasLoggedToSentry`, for nested errors)
 */
export const checkWasLoggedToSentry = (error: unknown): boolean => {
  const errorsToCheck = [error]

  // sometimes useFetch has server error nested in `error.data`,
  // so we have to check both for the flag
  if (error && typeof error === 'object' && 'data' in error && error.data) {
    errorsToCheck.push(error.data)
  }

  for (const e of errorsToCheck) {
    // having a horrible time with unknown TS type
    const wasLoggedToSentry = Boolean(
      e &&
        typeof e === 'object' &&
        'data' in e &&
        e.data &&
        typeof e.data === 'object' &&
        'wasLoggedToSentry' in e.data &&
        e.data.wasLoggedToSentry,
    )

    if (wasLoggedToSentry) {
      return true
    }
  }

  return false
}

/**
 * Helps TS to convert `error.data?: unknown` to `error.data?: { wasLoggedToSentry?: boolean }`
 */
const formattedErrorGuard = (error: { data?: unknown }): error is FormattedError => {
  if (!error.data) {
    return true
  }

  if (typeof error.data !== 'object') {
    return false
  }

  if (!('wasLoggedToSentry' in error.data)) {
    return true
  }

  if (
    typeof error.data.wasLoggedToSentry === 'undefined' ||
    typeof error.data.wasLoggedToSentry === 'boolean'
  ) {
    return true
  }

  return false
}

const generateError = (err: unknown): ErrorForNuxt => {
  const error: ErrorForNuxt = {
    ...(typeof err === 'object' && err),
    status: 500,
  }

  if (typeof err === 'string') {
    error.statusText = err
    return error
  }
  if (!err || typeof err !== 'object') {
    return error
  }

  if ('status' in err && typeof err.status === 'number') {
    error.status = err.status
  } else if ('statusCode' in err && typeof err.statusCode === 'number') {
    error.status = err.statusCode
  }

  if ('message' in err && typeof err.message === 'string') {
    error.message = err.message
    error.statusText = err.message
  }

  if ('statusText' in err && typeof err.statusText === 'string') {
    error.statusText = err.statusText
  } else if ('statusMessage' in err && typeof err.statusMessage === 'string') {
    error.statusText = err.statusMessage
  }

  return error
}

export const formatError = (
  err: unknown,
  {
    shouldKeepStackAndCause = false,
    shouldKeepMessage = false,
    override = {},
  } = {},
): FormattedError => {
  const error = createError({ ...generateError(err), ...override })

  let newErrorData: AnyErrorData = {
    ...(typeof error.data === 'object' && error.data),
  }

  // by default, we do not want stack/cause/other data to be visible to the user
  if (!shouldKeepStackAndCause) {
    error.stack = undefined
    error.cause = undefined
    // only keep the wasLoggedToSentry in data
    newErrorData = { wasLoggedToSentry: newErrorData.wasLoggedToSentry }
  }

  // by default, we do not want to uncover the raw error message to the user
  if (!shouldKeepMessage) {
    const genericMessage =
      ERROR_MESSAGE_BY_STATUS_CODE[error.status ?? 500] || BASE_ERROR_MESSAGE
    error.message = genericMessage
    error.statusText = genericMessage
  }

  error.data = newErrorData
  if (formattedErrorGuard(error)) {
    return error
  }
  // This error will never happen, because formattedErrorGuard
  // accepts any error with error.data object (which we assign right above it)
  throw new Error('Failed to format error')
}

export const generateInternalServerError = ({
  errorText,
}: {
  errorText: string
}) =>
  createError({
    status: 500,
    statusText: `Internal server error: ${errorText}`,
  })

// Safari/WKWebView names the document's top-level scope this in stack traces
const DOCUMENT_SCOPE_FUNCTION = 'global code'

/**
 * Detects errors thrown by scripts a surrounding native app or browser injects
 * into the page (merchant iOS app WKWebViews, Norton, ...). They run as inline
 * code in the document, so their stack is a single `global code` frame pointing
 * at the HTML document rather than one of our own bundles. The app itself is
 * unaffected — the injected script simply finds nothing to act on.
 */
export const isInjectedThirdPartyError = (event: SentryEvent): boolean => {
  if (event.exception?.values?.length !== 1) {
    return false
  }

  const [{ mechanism, stacktrace }] = event.exception.values

  if (mechanism?.handled !== false || stacktrace?.frames?.length !== 1) {
    return false
  }

  const { function: scope, filename } = stacktrace.frames[0]

  return scope === DOCUMENT_SCOPE_FUNCTION && typeof filename === 'string' && !filename.endsWith('.js')
}

// Short, whitespace-free tokens such as "Ma", "La`prod" (optionally prefixed by "Error: ")
const MINIFIED_MESSAGE = /^(Error: )?\S{2,8}$/

/**
 * Chrome on iOS injects scripts that reject promises with minified messages
 * ("Ma", "La`prod"). The stack is empty or only `[native code]` frames, never
 * one of our own bundles. The app itself is unaffected.
 * Same noise we already drop in the tracking app (tracking MR !953).
 */
export const isChromeIosInjectedNoise = (event: SentryEvent): boolean => {
  const values = event.exception?.values ?? []

  return (
    values.length > 0 &&
    values.every(({ value = '', mechanism, stacktrace }) => {
      const frames = stacktrace?.frames ?? []
      return (
        mechanism?.handled === false &&
        MINIFIED_MESSAGE.test(value) &&
        frames.every(({ filename }) => !filename || filename === '[native code]')
      )
    })
  )
}

export const shouldLogError = (error: unknown): boolean => {
  if (!error) {
    return false
  }
  if (typeof error !== 'object') {
    // log error (it can be a string)
    return true
  }

  // Check this flag, to see if already handled (logged) this error
  if (checkWasLoggedToSentry(error)) {
    return false
  }

  const { status } = generateError(error)

  // log if it does not have a code, or code is not in IGNORE_ERROR_CODES
  return Boolean(!status || !IGNORE_ERROR_CODES.includes(status))
}

export const suppressVueRouterErrors = once((): void => {
  if (typeof console === 'undefined') {
    return
  }

  const { warn: consoleWarn } = console

  const ignoredWarnings = [
    '[Vue Router warn]: No match found for location with path',
    '[Vue Router warn]: Location',
  ]

  // eslint-disable-next-line no-console
  console.warn = (...args: unknown[]): void => {
    const isVueRouterWarning = ignoredWarnings.some(prefix => typeof args[0] === 'string' && args[0].startsWith(prefix))
    if (isVueRouterWarning) {
      return
    }

    consoleWarn(...args)
  }
})
